import { ContactDialog } from '@/components/marketing/contact-dialog';
import { useSession } from '@/lib/auth-client';
import '@/styles/landing.css';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';

export const Route = createFileRoute('/')({
    component: LandingPage,
});

const EASE: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

const fadeUp = {
    hidden: { opacity: 0, y: 22 },
    show: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, ease: EASE },
    },
};

const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.09 } },
};

const vp = { once: true, margin: '-80px' as const };

const FEATURES = [
    {
        n: 'i.',
        title: (
            <>
                Every listing, <em>at a glance.</em>
            </>
        ),
        body: 'Cost, fees, payout, age, and platform — all on one row. No more hunting across nine spreadsheet tabs to remember what a piece actually owes you.',
        ticket: ['Inventory', 'Live'],
    },
    {
        n: 'ii.',
        title: (
            <>
                Price it right, <em>before you list.</em>
            </>
        ),
        body: "The built-in Pricing Calculator breaks down platform fees, net profit, and break-even across all 7 platforms at once. Factor in purchase tax and acquisition costs, set a target ROI, and know exactly what you'll make — before you publish.",
        ticket: ['Calculator', 'Built In'],
    },
    {
        n: 'iii.',
        title: (
            <>
                Know your numbers, <em>all year.</em>
            </>
        ),
        body: "Sell-through patterns surface what's moving and flag what's gone stale. Your 1099-K threshold tracks in real time, and tax estimates on every sale mean no surprises come April.",
        ticket: ['Insights', 'Tax Aware'],
    },
];

const STEPS = [
    {
        n: '01',
        t: 'Source it.',
        b: 'Snap the tag, drop the cost, note the bin. Stockwright builds the SKU before the piece even hits a hanger.',
    },
    {
        n: '02',
        t: 'Price it.',
        b: 'Run the numbers before you list. The Pricing Calculator shows estimated fees, net profit, break-even, and shipping cost across every platform — so you set the right price the first time.',
    },
    {
        n: '03',
        t: 'Sell it.',
        b: 'Record the sale with actual fees, shipping, and payout. The profit number you see is the real one — every platform, every time.',
    },
    {
        n: '04',
        t: 'Restock smart.',
        b: 'End-of-week reports show what to relist, what to mark down, and what to source more of. The system learns your shop.',
    },
];

const PLATFORMS = [
    { name: 'Poshmark', meta: 'Live now', soon: false },
    { name: 'eBay', meta: 'Live now', soon: false },
    { name: 'Depop', meta: 'Live now', soon: false },
    { name: 'Mercari', meta: 'Live now', soon: false },
    { name: 'Etsy', meta: 'Live now', soon: false },
    { name: 'Vinted', meta: 'Live now', soon: false },
    {
        name: 'Facebook Marketplace',
        meta: 'Live now',
        soon: false,
    },
    { name: 'Shopify', meta: 'Coming soon', soon: true },
];

const QUOTES = [
    {
        q: (
            <>
                &ldquo;We finally know <em>what to list next</em> before it
                piles up. That&rsquo;s the difference.&rdquo;
            </>
        ),
        who: 'Jessica Langley',
        role: 'Vintage curator · Brooklyn',
        stat: '33%',
        statLabel: 'Lift in sell-through',
    },
    {
        q: (
            <>
                &ldquo;I used to spend Sunday nights wrestling a spreadsheet.
                Now I spend them <em>watching a movie.</em>&rdquo;
            </>
        ),
        who: 'Marcus Reed',
        role: 'Full-time reseller · 6 yrs',
        stat: '8 hrs',
        statLabel: 'Saved each week',
    },
    {
        q: (
            <>
                &ldquo;It&rsquo;s the first inventory tool that{' '}
                <em>respects how I actually work</em> on a Tuesday
                afternoon.&rdquo;
            </>
        ),
        who: 'Priya Shankar',
        role: 'Thrift flipper · Austin',
        stat: '412',
        statLabel: 'Pieces, one workspace',
    },
];

const FAQS = [
    {
        q: 'Will Stockwright actually save me time, or is it just another tool to learn?',
        a: 'Most resellers are listing in under 4 minutes per piece by week two. The whole point is fewer tabs, fewer columns, fewer Sunday nights — not another dashboard to babysit.',
    },
    // {
    //     q: 'How does the platform sync work?',
    //     a: 'We connect directly to Poshmark, eBay, Depop, Mercari, Etsy, Vinted, and Facebook Marketplace. List once; it lands clean on every channel. Sales, fees, and payouts pull back automatically — no copy/paste.',
    // },
    {
        q: 'Is my COGS and sourcing data really private?',
        a: 'Always. Your inventory, costs, and customer data are encrypted, never resold, and never shared with anyone — including other sellers. We make money from your subscription, not your data.',
    },
    {
        q: 'What happens to my data if I cancel?',
        a: "Export everything to CSV anytime — listings, sales, COGS, the works. If you cancel we keep your account in cold storage for 90 days in case you want it back, then it's deleted clean.",
    },
    {
        q: 'Does Stockwright help with taxes?',
        a: 'Yes — Stockwright tracks your 1099-K threshold in real time so you always know where you stand. Set your income tax rate in Account Settings and every sale shows an estimated SE tax and income tax hit alongside your net profit. These are planning tools, not tax filing. Consult your accountant for the real thing.',
    },
    {
        q: "I'm coming from a spreadsheet. Will my data come over?",
        a: "Yes — drop in a CSV from any spreadsheet and we'll map it. Most sellers are fully migrated in under 30 minutes. We'll help if you get stuck.",
    },
];

function LandingPage() {
    const navigate = useNavigate();
    const { data: session, isPending } = useSession();
    const [openFaq, setOpenFaq] = useState(-1);
    // const [isAlertVisible, setIsAlertVisible] = useState(true);
    const videoRef = useRef<HTMLVideoElement>(null);

    const heroSource = useMemo(() => {
        const w = window.innerWidth;

        if (w < 900) {
            return 'https://res.cloudinary.com/flannery38/video/upload/q_auto,w_1280,ac_none/v1778013296/stockwright/6120425-uhd_4096_2160_25fps.mp4';
        }

        return 'https://res.cloudinary.com/flannery38/video/upload/v1778013296/stockwright/6120425-uhd_4096_2160_25fps.mp4';
    }, []);

    function handleAnchorClick(e: MouseEvent<HTMLAnchorElement>) {
        const href = e.currentTarget.getAttribute('href');
        if (!href?.startsWith('#')) return;
        const el = document.querySelector(href);
        if (!el) return;
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    useEffect(() => {
        if (isPending || !session?.user) return;
        navigate({ to: '/dashboard', replace: true });
    }, [isPending, session?.user, navigate]);

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        const saved = localStorage.getItem('sw_hero_t');
        if (saved) {
            try {
                v.currentTime = parseFloat(saved) || 0;
            } catch {
                //
            }
        }
        const onTime = () =>
            localStorage.setItem('sw_hero_t', String(v.currentTime));
        v.addEventListener('timeupdate', onTime);
        return () => v.removeEventListener('timeupdate', onTime);
    }, []);

    return (
        <div className="sw-lp min-h-[90vh] light">
            <header className="lp-hero">
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background:
                            'linear-gradient(180deg, #1a3a2a 0%, #0a2118 100%)',
                        zIndex: 0,
                    }}
                />
                <video
                    ref={videoRef}
                    className="lp-hero-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    poster="https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?auto=format&fit=crop&w=1440&q=60"
                    onError={(e) => {
                        (e.target as HTMLVideoElement).style.display = 'none';
                    }}
                >
                    <source src={heroSource} type="video/mp4" />
                </video>
                <div className="lp-hero-overlay" />

                <nav className="lp-nav">
                    <img
                        src="https://res.cloudinary.com/flannery38/image/upload/v1771973120/stockwright/logo-white.svg"
                        alt="Stockwright"
                        style={{
                            width: 'auto',
                            filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
                        }}
                        className="h-8 lg:h-9 w-auto"
                    />
                    <div className="lp-nav-links">
                        <a href="#features" onClick={handleAnchorClick}>
                            What it does
                        </a>
                        <a href="#workflow" onClick={handleAnchorClick}>
                            Workflow
                        </a>
                        <a href="#pricing" onClick={handleAnchorClick}>
                            Pricing
                        </a>
                        <a href="#faq" onClick={handleAnchorClick}>
                            FAQ
                        </a>
                    </div>
                    <Link
                        to="/auth"
                        search={{ mode: 'signup' }}
                        className="lp-nav-cta"
                    >
                        Start free trial →
                    </Link>
                </nav>

                <div className="lp-hero-content">
                    <h1 className="lp-hero-headline">
                        Sell more.
                        <br />
                        <em>Stress less.</em>
                    </h1>
                    <div className="lp-hero-side">
                        <div className="lp-stamp">Est. for resellers</div>
                        <div>
                            The quiet inventory tool for closets, racks, and
                            resale stacks. Built by sellers who got tired of the
                            spreadsheet.
                        </div>
                    </div>
                </div>
            </header>

            <section
                id="features"
                className="lp-section"
                style={{ background: 'var(--lp-paper)' }}
            >
                <div className="lp-container">
                    <motion.div
                        className="lp-section-head"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        <div className="lp-eyebrow">— What it does · No 01</div>
                        <h2>
                            A calm shop floor for the way <em>you</em> already
                            work.
                        </h2>
                        <p>
                            Stockwright replaces the spreadsheet, the notes app,
                            and the platform tabs with one quiet workspace.
                            Three things it does first, and does well.
                        </p>
                    </motion.div>
                    <motion.div
                        className="lp-features-grid"
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        {FEATURES.map((item, i) => (
                            <motion.div
                                key={i}
                                className="lp-feature-card"
                                variants={fadeUp}
                            >
                                <div className="lp-feature-num">{item.n}</div>
                                <h3>{item.title}</h3>
                                <p>{item.body}</p>
                                <div className="lp-ticket">
                                    <span>{item.ticket[0]}</span>
                                    <span>{item.ticket[1]}</span>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <div style={{ background: 'var(--lp-paper)' }}>
                <div className="lp-divider">The workflow · No 02</div>
            </div>

            <section id="workflow" className="lp-section lp-workflow">
                <div className="lp-container">
                    <motion.div
                        className="lp-section-head"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        <div className="lp-eyebrow">
                            — A reseller's day · No 02
                        </div>
                        <h2>
                            Five steps. <em>One quiet system.</em>
                        </h2>
                        <p>
                            From the moment a piece is sourced to the moment it
                            ships — Stockwright keeps the whole loop tight
                            without ever feeling busy.
                        </p>
                    </motion.div>
                    <div className="lp-workflow-grid">
                        <motion.div
                            className="lp-workflow-steps"
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={vp}
                        >
                            {STEPS.map((step, i) => (
                                <motion.div
                                    key={i}
                                    className="lp-step"
                                    variants={fadeUp}
                                >
                                    <div className="lp-step-num">{step.n}</div>
                                    <div>
                                        <h4>{step.t}</h4>
                                        <p>{step.b}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                        <motion.div
                            className="lp-workflow-aside"
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={vp}
                        >
                            <div className="lp-workflow-img">
                                <img
                                    src="https://res.cloudinary.com/flannery38/image/upload/f_auto,q_auto/v1772034781/stockwright/pexels-photo-1148960.jpg"
                                    alt="Reseller workspace"
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <div className="lp-workflow-meta">
                                <span>Lot · Spring &rsquo;26</span>
                                <span>Tag № 0241</span>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            <section className="lp-section lp-dashboard">
                <div className="lp-container">
                    <motion.div
                        className="lp-section-head"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        <div className="lp-eyebrow">
                            — The workspace · No 03
                        </div>
                        <h2>
                            Built like the floor of a <em>well-run shop.</em>
                        </h2>
                        <p>
                            Designed to sit open all day — calm, scannable, and
                            honest about the numbers. A peek at the inventory
                            view.
                        </p>
                    </motion.div>
                    <motion.div
                        className="w-full"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        <div className="bg-transparent w-full aspect-5/3 relative -my-14">
                            <img
                                src="https://res.cloudinary.com/flannery38/image/upload/v1778704186/stockwright/625shots_so.png"
                                alt=""
                                className="w-full aspect-4/3 object-cover object-top absolute top-1/2 left-1/2 -translate-1/2"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                    </motion.div>
                </div>
            </section>

            <section
                id="integrations"
                className="lp-section"
                style={{ background: 'var(--lp-paper)' }}
            >
                <div className="lp-container">
                    <div className="lp-integrations-grid">
                        <motion.div
                            variants={fadeUp}
                            initial="hidden"
                            whileInView="show"
                            viewport={vp}
                        >
                            <div
                                className="lp-eyebrow"
                                style={{ marginBottom: 14 }}
                            >
                                — The stack · No 04
                            </div>
                            <h2
                                style={{
                                    fontSize: 'clamp(36px, 4.4vw, 56px)',
                                    lineHeight: 1.02,
                                    marginBottom: 18,
                                }}
                            >
                                Tracks where{' '}
                                <em
                                    style={{
                                        fontStyle: 'italic',
                                        color: 'var(--lp-walnut)',
                                        fontWeight: 400,
                                    }}
                                >
                                    you
                                </em>{' '}
                                already sell.
                            </h2>
                            <p
                                style={{
                                    color: 'var(--lp-ink-soft)',
                                    fontSize: 17,
                                    maxWidth: 460,
                                    lineHeight: 1.55,
                                }}
                            >
                                Keep selling on the platforms you already know.
                                Stockwright sits alongside them — tracking your
                                inventory, real fees, and true profit in one
                                place so you always know where you stand.
                            </p>
                            <div
                                style={{
                                    marginTop: 28,
                                    fontFamily: "'JetBrains Mono', monospace",
                                    fontSize: 11,
                                    letterSpacing: '0.15em',
                                    textTransform: 'uppercase',
                                    color: 'var(--lp-walnut)',
                                }}
                            >
                                All 7 platforms · One workspace
                            </div>
                        </motion.div>
                        <motion.div
                            className="lp-platform-list"
                            variants={stagger}
                            initial="hidden"
                            whileInView="show"
                            viewport={vp}
                        >
                            {PLATFORMS.map((p, i) => (
                                <motion.div
                                    key={i}
                                    className="lp-platform"
                                    variants={fadeUp}
                                >
                                    <span className="name">{p.name}</span>
                                    <span
                                        className={`meta${p.soon ? ' soon' : ''}`}
                                    >
                                        {p.meta}
                                    </span>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </div>
            </section>

            <section className="lp-section lp-testimonials">
                <div className="lp-container">
                    <motion.div
                        className="lp-section-head"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        <div className="lp-eyebrow">
                            — Sellers, in their words · No 05
                        </div>
                        <h2>
                            Loved by people who <em>actually do this</em> for a
                            living.
                        </h2>
                    </motion.div>
                    <motion.div
                        className="lp-quote-grid"
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        {QUOTES.map((q, i) => (
                            <motion.div
                                key={i}
                                className="lp-quote-card"
                                variants={fadeUp}
                            >
                                <div className="lp-quote-mark">&ldquo;</div>
                                <blockquote>{q.q}</blockquote>
                                <div className="lp-quote-foot">
                                    <div className="lp-quote-author">
                                        {q.who}
                                        <span>{q.role}</span>
                                    </div>
                                    <div className="lp-quote-stat">
                                        {q.stat}
                                        <span>{q.statLabel}</span>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <section id="pricing" className="lp-section lp-pricing">
                <div className="lp-container">
                    <motion.div
                        className="lp-section-head"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        <div className="lp-eyebrow">— Plans · No 06</div>
                        <h2>
                            Priced for resellers, <em>not enterprise.</em>
                        </h2>
                        <p>
                            Start with a 14-day free trial. Card required.
                            Founding members lock in early pricing for life and
                            get priority onboarding.
                        </p>
                    </motion.div>
                    <motion.div
                        className="lp-pricing-grid"
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        {/* Starter — featured */}
                        <motion.div
                            className="lp-price-card featured"
                            variants={fadeUp}
                        >
                            <span className="lp-featured-tag">
                                Most popular
                            </span>
                            <div className="lp-price-tier">Starter</div>
                            <div className="lp-price-name">
                                For the side hustle
                            </div>
                            <div className="lp-price-amount">
                                <span className="num-big">$19</span>
                                <span className="per">.99 / month</span>
                            </div>
                            <div className="lp-price-desc">
                                Unlimited inventory items, every platform, the
                                full insights stack. Built for the seller moving
                                inventory every week.
                            </div>
                            <ul className="lp-price-features">
                                <li>Unlimited inventory items</li>
                                <li>All 7 platforms connected</li>
                                <li>Real profit &amp; margin tracking</li>
                                <li>Sell-through &amp; aging insights</li>
                            </ul>
                            <Link
                                to="/auth"
                                search={{ mode: 'signup' }}
                                className="lp-price-cta primary"
                            >
                                Start free trial →
                            </Link>
                        </motion.div>

                        {/* Pro */}
                        <motion.div
                            className="lp-price-card lp-price-card--coming-soon"
                            variants={fadeUp}
                        >
                            <span className="lp-featured-tag lp-featured-tag--coming-soon">
                                Coming Soon
                            </span>
                            <div className="lp-price-tier">Pro</div>
                            <div className="lp-price-name">
                                <em>For full-time</em> sellers
                            </div>
                            <div className="lp-price-amount">
                                <span className="num-big">$29</span>
                                <span className="per">.99 / month</span>
                            </div>
                            <div className="lp-price-desc">
                                Multi-platform cross listing. Perfect for the
                                small business level resellers.
                            </div>
                            <ul className="lp-price-features">
                                <li>Everything in starter</li>
                                <li>Weekly sell-through report</li>
                                <li>Multi-platform listing management</li>
                            </ul>
                        </motion.div>

                        {/* Team */}
                        <motion.div className="lp-price-card" variants={fadeUp}>
                            <div className="lp-price-tier">Team</div>
                            <div className="lp-price-name">
                                For shops &amp; crews
                            </div>
                            <div className="lp-price-amount">
                                <span className="num-big">Custom</span>
                            </div>
                            <div className="lp-price-desc">
                                Multi-user closets, role-based access, and
                                dedicated onboarding. For resale teams and
                                family operations.
                            </div>
                            <ul className="lp-price-features">
                                <li>Everything in starter</li>
                                <li>Unlimited team seats</li>
                                <li>Roles &amp; permissions</li>
                                <li>Dedicated onboarding</li>
                                <li>Priority support</li>
                            </ul>
                            <ContactDialog
                                triggerLabel="Talk to us →"
                                triggerClassName="lp-price-cta"
                            />
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            <section id="faq" className="lp-section lp-faq">
                <div className="lp-container-narrow">
                    <motion.div
                        className="lp-section-head"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        <div className="lp-eyebrow">— Questions · No 07</div>
                        <h2>Anything else?</h2>
                    </motion.div>
                    <motion.div
                        className="lp-faq-list"
                        variants={stagger}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        {FAQS.map((item, i) => (
                            <motion.div
                                key={i}
                                className={`lp-faq-item${openFaq === i ? ' open' : ''}`}
                                variants={fadeUp}
                            >
                                <button
                                    className="lp-faq-q"
                                    onClick={() =>
                                        setOpenFaq(openFaq === i ? -1 : i)
                                    }
                                >
                                    {item.q}
                                    <span className="lp-faq-toggle">+</span>
                                </button>
                                <div className="lp-faq-a">{item.a}</div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <div className="lp-final-cta">
                <div className="lp-final-cta-inner">
                    <motion.div
                        className="lp-final-cta-content"
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="show"
                        viewport={vp}
                    >
                        <div>
                            <div
                                className="lp-eyebrow"
                                style={{
                                    color: 'var(--lp-brass)',
                                    marginBottom: 16,
                                }}
                            >
                                — Founding members · No 08
                            </div>
                            <h2>
                                Run your shop the way
                                <br />
                                it <em>deserves</em> to be run.
                            </h2>
                            <p>
                                Fourteen days free. Lock founding-member pricing
                                before launch — it&rsquo;s the lowest
                                it&rsquo;ll ever be.
                            </p>
                        </div>
                        <Link
                            to="/auth"
                            search={{ mode: 'signup' }}
                            className="lp-cta-btn"
                        >
                            Start free trial →
                        </Link>
                    </motion.div>
                </div>
            </div>

            <footer className="lp-footer pt-16 px-10 pb-24">
                <div className="lp-container">
                    <div className="lp-footer-grid">
                        <div className="lp-footer-brand">
                            <img
                                src="https://res.cloudinary.com/flannery38/image/upload/v1771973120/stockwright/logo.svg"
                                alt="Stockwright"
                                loading="lazy"
                                decoding="async"
                            />
                            <p className="lp-footer-tag">
                                A calm, honest workspace for the resale business
                                you&rsquo;ve quietly been building. Sell more.
                                Stress less.
                            </p>
                        </div>
                        <div className="lp-footer-col">
                            <h5>Product</h5>
                            <a href="#features" onClick={handleAnchorClick}>
                                What it does
                            </a>
                            <a href="#workflow" onClick={handleAnchorClick}>
                                Workflow
                            </a>
                            <a href="#integrations" onClick={handleAnchorClick}>
                                Integrations
                            </a>
                            <a href="#pricing" onClick={handleAnchorClick}>
                                Pricing
                            </a>
                        </div>
                        <div className="lp-footer-col">
                            <h5>Company</h5>
                            <Link to="/about">About</Link>
                        </div>
                        <div className="lp-footer-col">
                            <h5>Help</h5>
                            <Link to="/faq">FAQ</Link>
                            <Link to="/terms">Terms</Link>
                            <Link to="/privacy-policy">Privacy</Link>
                            <Link to="/security">Security</Link>
                        </div>
                    </div>
                    <div className="lp-footer-bottom">
                        <span>
                            © {new Date().getFullYear()} Stockwright · Made for
                            resellers
                        </span>
                        <span>v1.0.1</span>
                    </div>
                </div>
            </footer>
            {/* <div className="fixed bottom-2 left-1/2 -translate-x-1/2 w-full px-2">
                {isAlertVisible && (
                    <Alert className="bg-(--lp-rust) border-(--lp-rust-deep)">
                        <InfoIcon />
                        <AlertTitle className="font-bold">Heads up!</AlertTitle>
                        <AlertDescription className="text-white">
                            Stockwright is currently in beta. You're welcome to
                            sign up and even upgrade to Pro, though you may
                            occasionally encounter minor hiccups as we put the
                            finishing touches on things. Thanks for your
                            patience and support!
                        </AlertDescription>
                        <AlertAction>
                            <Button
                                variant="link"
                                className="text-white"
                                onClick={() => {
                                    setIsAlertVisible(false);
                                }}
                            >
                                <X />
                            </Button>
                        </AlertAction>
                    </Alert>
                )}
            </div> */}
        </div>
    );
}
